'use client';

import { useMemo } from 'react';

import { usePages } from '@/api/usePages';
import { IBlock, PageResponse } from '@/lib/types';
import { useAppState } from '@/state/hooks/app.state.hook';

interface IUseRawBlocksProps {
  slug: string;
  /** The page as the server fetched it, when the route did so. */
  initialPage?: PageResponse;
  /** The locale the server fetched it in. */
  initialPageLocale?: string;
}

interface IUseRawBlocksReturn {
  rawBlocks: IBlock[] | null;
  isLoading: boolean;
  /** The blocks are the server's page, already in the document. */
  isServerPage: boolean;
}

/**
 * Hook that fetches page data by slug and extracts the raw blocks array.
 *
 * @example
 * const { rawBlocks, isLoading } = useRawBlocks({ slug: 'home' });
 */
export const useRawBlocks = ({
  slug,
  initialPage,
  initialPageLocale,
}: IUseRawBlocksProps): IUseRawBlocksReturn => {
  const { initialBoot } = useAppState();

  const { getSinglePageBySlug } = usePages({
    pageSlug: slug,
    portalId: initialBoot?.portalId,
    initialPage,
    initialPageLocale,
  });

  const rawBlocks = useMemo((): IBlock[] | null => {
    const pageData = getSinglePageBySlug.data;
    if (!pageData) return null;

    return pageData.blocksV2 ?? null;
  }, [getSinglePageBySlug.data]);

  const isLoading =
    getSinglePageBySlug.isLoading || getSinglePageBySlug.isPlaceholderData;

  return {
    rawBlocks,
    isLoading,
    isServerPage: initialPage !== undefined && getSinglePageBySlug.data === initialPage,
  };
};
